/**
 * Number formatting for bench-science results.
 *
 * Lab quantities run from fmol to mg and from 10 cells/mL to 10⁷, so a fixed number of
 * decimals reads badly at one end or the other. These helpers round to significant
 * digits for results and echo typed intermediates without float noise.
 */

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const SCIENTIFIC_LOW = 1e-3;
const SCIENTIFIC_HIGH = 1e6;
const MAX_SIG = 21;

/** An integer exponent in Unicode superscripts: −8 → "⁻⁸". */
export function superscriptInt(value: number): string {
  let out = value < 0 ? "⁻" : "";
  for (const digit of String(Math.abs(value))) {
    out += SUPERSCRIPT_DIGITS[Number(digit)];
  }
  return out;
}

/** `2.58 × 10⁷`, with the mantissa in the reader's decimal mark. */
export function scientific(
  fmt: NumberFormatter,
  value: number,
  digits: number
): string {
  const [mantissa, exponent] = value
    .toExponential(Math.max(0, digits - 1))
    .split("e");
  const shown = fmt(Number(mantissa), {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.max(0, digits - 1),
  });
  return `${shown} × 10${superscriptInt(Number(exponent))}`;
}

/**
 * A result rounded to `digits` significant digits (trailing zeros dropped): plain for
 * everyday magnitudes, scientific notation below 0.001 or from a million up.
 */
export function sig(fmt: NumberFormatter, value: number, digits = 4): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const clamped = Math.min(Math.max(Math.round(digits), 1), MAX_SIG);
  const magnitude = Math.abs(value);
  if (magnitude < SCIENTIFIC_LOW || magnitude >= SCIENTIFIC_HIGH) {
    return scientific(fmt, value, clamped);
  }
  return fmt(value, { maximumSignificantDigits: clamped });
}

/** Fixed decimal places, no grouping: 1.856. */
export function fixed(
  fmt: NumberFormatter,
  value: number,
  places: number
): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return fmt(value, {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
    useGrouping: false,
  });
}

/** A typed or intermediate value echoed without float noise (0.13, not 0.1300000007). */
export function plain(fmt: NumberFormatter, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return fmt(value, { maximumSignificantDigits: 10 });
}

/** Round to `digits` significant digits as a number — for text that is copied. */
export function roundSig(value: number, digits: number): number {
  if (!Number.isFinite(value) || value === 0) {
    return value;
  }
  return Number(value.toPrecision(digits));
}

/** Tab-separated rows, which paste into a spreadsheet or a lab notebook as cells. */
export function toTsv(
  head: readonly string[],
  rows: readonly (readonly string[])[]
): string {
  return [head, ...rows].map((row) => row.join("\t")).join("\n");
}
